const getFieldValue = (
  item: Record<string, unknown>,
  field: string,
): unknown => {
  const keys = field.split('.');
  let value: unknown = item;
  for (const key of keys) {
    value = (value as Record<string, unknown>)?.[key];
  }
  return value;
};

/**
 * Human-readable label for filter option values (e.g. enum strings).
 *
 * @param option - The option value to format.
 * @returns The formatted option label.
 */
export function formatFilterOptionLabel(option: string): string {
  if (!option.includes('_')) {
    // Make the first letter of the first word uppercase and the rest of the words lowercase.
    return option.charAt(0).toUpperCase() + option.slice(1).toLowerCase();
  }

  // Split the option by underscores and capitalize the first letter of each word.
  return option
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Creates getOptions for a simple field-based filter.
 * Use for filters where options are derived from the dataset by a field path.
 */
export const createFieldFilterOptions =
  <T extends object>(field: string) =>
  (data: T[]): string[] =>
    Array.from(
      new Set(
        (data || [])
          .map((item) => getFieldValue(item as Record<string, unknown>, field))
          .filter((v) => v != null && v !== '')
          .map((v) => String(v)),
      ),
    ).sort();

/**
 * Creates getValueFromItem for a simple field-based filter.
 * Use for matching items against selected filter values.
 */
export const createFieldFilterValue =
  <T extends object>(field: string) =>
  (item: T): string[] => {
    const value = getFieldValue(item as Record<string, unknown>, field);
    return value != null && value !== '' ? [String(value)] : [];
  };

export interface FieldFilterConfig<T> {
  key: string;
  title: string;
  getOptions: (data: T[]) => string[];
  getValueFromItem: (item: T) => string[];
}

/**
 * Creates a complete filter config for a simple field-based filter.
 * Use when options and values are derived from a single field path.
 */
export const createFieldFilterConfig = <T extends object>(
  key: string,
  title: string,
  field: string,
): FieldFilterConfig<T> => ({
  key,
  title,
  getOptions: createFieldFilterOptions<T>(field),
  getValueFromItem: createFieldFilterValue<T>(field),
});
